import { useEffect, useMemo, useState } from "react";

import { RefreshCwIcon } from "lucide-react";
import { CustomTableMenuItem, Table } from "@monolith-forensics/monolith-ui";

import { isReportGenerating } from "../savedReports.utils";
import { SavedReport } from "../savedReports.types";
import { getSavedReportColumnDefs } from "./SavedReportColumnDefinitions";

// Table area plus the table menu row above it
const TABLE_HEIGHT = 390;

interface SavedReportsTableProps {
  data: SavedReport[];
  loading?: boolean;
  canManage: boolean;
  saving?: boolean;
  onDelete: (report: SavedReport) => void;
  onSave: () => void;
  onRefresh: () => void;
}

export const SavedReportsTable: React.FC<SavedReportsTableProps> = ({
  data,
  loading,
  canManage,
  saving,
  onDelete,
  onSave,
  onRefresh,
}) => {
  const columnProps = useMemo(
    () => getSavedReportColumnDefs({ canManage, onDelete }),
    [canManage, onDelete]
  );

  // One report at a time: Save Report waits while a recent report is
  // generating. Rows older than the block window do not count, so a build that
  // never finished cannot lock Save Report for everyone on the report.
  const anyPending = data.some((report) => report.is_ready === 0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!anyPending) return;
    // Tick so the window can expire even when the polled data is unchanged
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [anyPending]);
  const generating = data.some((report) => isReportGenerating(report, now));

  // Refresh and Save Report live in the table menu instead of a card header
  const tableMenuOptions = useMemo(
    () => ({
      enabled: true,
      addButtonOptions: {
        enabled: true,
        label: "Save Report",
        onClick: onSave,
        buttonOptions: {
          // One state for the whole save and generate cycle: disabled, never
          // switching to a spinner (the generating row already shows one)
          disabled: saving || generating,
          title: generating ? "A report is still generating" : undefined,
        },
      },
      customMenuItems: [
        {
          type: "button" as const,
          position: "right" as const,
          options: {
            children: <RefreshCwIcon size={14} />,
            style: { padding: "0px 4px" },
            title: "Refresh",
            onClick: onRefresh,
          },
        },
      ] as CustomTableMenuItem[],
    }),
    [onSave, onRefresh, saving, generating]
  );

  // The monolith-ui Table container is a flex child with height 0 that fills
  // its parent, so the parent must be a flex column with an explicit height.
  // A height prop on the Table alone leaves the container collapsed to 0.
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: TABLE_HEIGHT,
      }}
    >
      <Table
        keyField="saved_report_id"
        data={data}
        columnProps={columnProps}
        loading={loading}
        virtualized
        enableSorting
        tableMenuOptions={tableMenuOptions}
      />
    </div>
  );
};
